import { computed, onMounted, onUnmounted, ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import { useDeviceStore } from '@/stores/device'
import { storeToRefs } from 'pinia'
import { deviceCalibration } from '@rapoo/device-sdk'
import type { ClassicKeyTravel } from '@rapoo/device-sdk'
import { useKeyboardReport } from '../useKeyboardReport'
import { defaultReportError } from '@/composables/useErrorLogger'

type UseDemoModeOptions = {
  getKeyTravel: (index: number) => ClassicKeyTravel
  /** 仅在演示模式开启时进入 demo / 监听上报 */
  enabled: MaybeRefOrGetter<boolean>
}

/**
 * 演示模式
 * 调用自动进入演示模式与上报数据监听
 *
 * 注意生命周期：本 composable 在页面外层调用时，须在 onMounted 再开 demo。
 * Vue 挂载顺序是「子 onMounted → 父 onMounted」；KeyboardPanel 里的
 * useCalibrationMode 会在子 onMounted 进入校准。若在 setup/immediate 抢先开 demo，
 * 随后校准会覆盖，实体键盘会卡在校准模式而失灵。
 */
export function useDemoMode(options: UseDemoModeOptions) {
  const { getKeyTravel } = options
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)
  const keyTravel = computed(() => currentDevice.value?.features.keyTravel)
  const enabled = computed(() => toValue(options.enabled))

  /* ------------ 演示模式 ------------ */
  const switchDemo = ref(false)

  const switchDemoMode = async (type = false) => {
    if (!keyTravel.value) {
      console.warn("Current device doesn't have keyTravel feature")
      defaultReportError("Current device doesn't have keyTravel feature")
      return
    }
    try {
      // 进入/退出 demo 前先关掉校准，避免与 KeyboardPanel 的校准态冲突
      await keyTravel.value.switchCalibrationMode(false)
      await keyTravel.value.switchDemoMode(type)
      switchDemo.value = type
    } catch (error) {
      console.error('switchDemoMode error', error)
      defaultReportError(error)
    }
  }

  // 父组件 onMounted 晚于子组件：此时再开 demo，才能盖过 KeyboardPanel 已打开的校准
  onMounted(() => {
    if (enabled.value) switchDemoMode(true)
  })

  watch(enabled, (on) => {
    switchDemoMode(on)
    if (!on) {
      realTimeTravel.value = 0
      activeKeyIndex.value = null
    }
  })

  // 设备 feature 晚于挂载就绪时补进 demo
  watch(keyTravel, (feature) => {
    if (feature && enabled.value && !switchDemo.value) {
      switchDemoMode(true)
    }
  })

  onUnmounted(() => {
    if (switchDemo.value) switchDemoMode(false)
  })
  /* ------------ 演示模式 ------------ */

  /* ------------ 硬件数据上报 ------------ */
  const realTimeTravel = ref(0)
  const activeKeyIndex = ref<number | null>(null)

  const activeAbsoluteTrigger = computed(() => {
    if (activeKeyIndex.value == null) return null
    return getKeyTravel(activeKeyIndex.value).absoluteTrigger
  })

  const activeAbsoluteTriggerNormalized = computed(() => {
    if (activeAbsoluteTrigger.value == null) return null
    return (activeAbsoluteTrigger.value / 330) * 255
  })

  const hasCrossedAbsoluteTrigger = computed(() => {
    if (activeAbsoluteTriggerNormalized.value == null) return false
    return realTimeTravel.value > activeAbsoluteTriggerNormalized.value
  })

  const dispatchDemoModeData = (calibData: deviceCalibration) => {
    if (!enabled.value) return
    realTimeTravel.value = calibData.NormalisedDelta ?? 0
    activeKeyIndex.value = typeof calibData.index === 'number' ? calibData.index : null
  }

  const { setup, teardown } = useKeyboardReport(dispatchDemoModeData)

  onMounted(() => {
    setup(dispatchDemoModeData)
  })

  onUnmounted(() => {
    teardown()
  })

  return {
    realTimeTravel,
    hasCrossedAbsoluteTrigger,
  }
}
